import { observer } from 'mobx-react-lite'
import React, { createContext, FC, KeyboardEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { components, MultiValueGenericProps, MultiValueProps } from 'react-select'
import styled from 'styled-components'

import { IDropDownOption } from '../../../store/dataModels/interfaces'
import AllSelect from './AllSelect'
import { ISelectOption } from './SelectInput.type'

// If you are using mobx, be sure to covert your options to JS
// before passing as props to this component
// const optionsToJs = toJS(options)
// const selectedOptionsToJs = toJS(selectedOptions)
interface IProps {
  id?: string | number
  isSearchable?: boolean
  options: IDropDownOption[]
  selectedOptions: IDropDownOption[]
  isMulti?: boolean
  placeHolderText?: string
  onChange?: (values: IDropDownOption[]) => any
  allowSelectAll?: boolean
  allOption: IDropDownOption // required, will brake if not passed
  // This disbles all options, you can still view them
  // do not pass a onChange function
  disabled?: boolean
  // if you want to disable a single option. You have to
  // pass the option with a disabled prop EG:
  // const options = [
  // [{label: "one", value: 1, disabled: true},
  // {label: "two", value: 2}]
  closeMenuOnSelect?: boolean
  menuPlacement?: string
  menuPosition?: string
}

type OptionKey = string

interface IChipFocusContext {
  registerRemoveButton: (key: OptionKey, element: HTMLElement | null) => void
  requestFocusAfterRemoval: (key: OptionKey | undefined) => void
}

// Lets chips hand over keyboard focus to a sibling chip after one is removed
const ChipFocusContext = createContext<IChipFocusContext | null>(null)

const getOptionKey = (option: ISelectOption): OptionKey => String(option.value)

const byDocumentPosition = (a: HTMLElement, b: HTMLElement) =>
  a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1

// Temporarily removes elements from the tab order so the browser's default Tab skips them
const skipInTabOrder = (elements: HTMLElement[]) => {
  const previous = elements.map((element) => [element, element.getAttribute('tabindex')] as const)
  elements.forEach((element) => element.setAttribute('tabindex', '-1'))

  setTimeout(() =>
    previous.forEach(([element, tabIndex]) =>
      tabIndex === null ? element.removeAttribute('tabindex') : element.setAttribute('tabindex', tabIndex)
    )
  )
}

const DropdownComponent: React.FC<IProps> = ({
  id,
  options,
  onChange,
  selectedOptions,
  isMulti = false,
  isSearchable = false,
  placeHolderText = '',
  allowSelectAll = false,
  allOption,
  closeMenuOnSelect = true,
}) => {
  const formatOptionLabel = useCallback(
    ({ isDisabled, label, value }) => {
      return (
        <div key={`${id}-${value}`} style={isDisabled ? { color: 'rgb(194, 201, 209)' } : {}}>
          {label}
        </div>
      )
    },
    [id]
  )

  const removeButtons = useRef(new Map<OptionKey, HTMLElement>())
  const pendingFocusKey = useRef<OptionKey>()

  const chipFocus = useMemo<IChipFocusContext>(
    () => ({
      registerRemoveButton: (key, element) => {
        if (element) {
          removeButtons.current.set(key, element)
        } else {
          removeButtons.current.delete(key)
        }
      },
      requestFocusAfterRemoval: (key) => {
        pendingFocusKey.current = key
      },
    }),
    []
  )

  // Runs after the removal has been rendered, so it overrides react-select moving focus
  // to the input. If no sibling chip remains, focus stays on the input.
  useEffect(() => {
    if (pendingFocusKey.current === undefined) {
      return
    }

    removeButtons.current.get(pendingFocusKey.current)?.focus()
    pendingFocusKey.current = undefined
  }, [selectedOptions])

  const wrapperRef = useRef<HTMLDivElement>(null)

  // Keyboard order: chip remove buttons -> clear-all button -> input.
  // The clear-all button and input are rendered by the design system Dropdown, so they are
  // looked up within this component's own wrapper.
  const getFocusOrder = useCallback(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) {
      return []
    }

    const chipButtons = Array.from(removeButtons.current.values())
      .filter((element) => wrapper.contains(element))
      .sort(byDocumentPosition)
    const clearAllButton = wrapper.querySelector<HTMLElement>('button')
    const input = wrapper.querySelector<HTMLElement>('input:not([type="hidden"])')

    return [...chipButtons, clearAllButton, input].filter((element): element is HTMLElement => !!element)
  }, [])

  const onKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) {
        return
      }

      const order = getFocusOrder()
      const currentIndex = order.indexOf(document.activeElement as HTMLElement)
      if (currentIndex === -1) {
        return
      }

      const target = order[currentIndex + (event.shiftKey ? -1 : 1)]
      if (target) {
        event.preventDefault()
        target.focus()

        return
      }

      // Leaving the dropdown: let the browser move focus, without stopping on our own elements
      skipInTabOrder(order.filter((element) => element !== document.activeElement))
    },
    [getFocusOrder]
  )

  return (
    <ChipFocusContext.Provider value={chipFocus}>
      <StyledDropdownWrapper ref={wrapperRef} onKeyDownCapture={onKeyDownCapture}>
        <AllSelect
          id={id}
          isSearchable={isSearchable}
          value={selectedOptions}
          onChange={onChange}
          isMulti={isMulti}
          options={options}
          placeholder={placeHolderText}
          components={{
            MultiValue: CustomMultiValue,
            MultiValueLabel: CustomMultiValueLabel,
            ValueContainer: CustomValueContainer,
          }}
          noOptionsMessage={() => null}
          hideSelectedOptions={false}
          allowSelectAll={allowSelectAll}
          allOption={allOption}
          closeMenuOnSelect={closeMenuOnSelect}
          menuPlacement="auto"
          menuPosition="absolute"
          formatOptionLabel={formatOptionLabel}
        />
      </StyledDropdownWrapper>
    </ChipFocusContext.Provider>
  )
}

const CustomValueContainer: FC<MultiValueGenericProps<ISelectOption, boolean>> = ({ children, ...props }: any) => {
  const allOption = props.options[0]

  const currentValues = props.getValue()
  let toBeRendered = children
  if (currentValues.some((val) => val.value === allOption.value)) {
    // Render "all" option and an input element
    toBeRendered = [[children[0][0]], children[1]]
  }

  return <components.ValueContainer {...props}>{toBeRendered}</components.ValueContainer>
}

const CustomMultiValue: FC<MultiValueProps<ISelectOption, boolean>> = (props) => {
  const [t] = useTranslation()
  const chipFocus = useContext(ChipFocusContext)
  const { removeProps, isDisabled, data, index, getValue } = props
  const optionKey = getOptionKey(data)

  const registerRemoveButton = useCallback(
    (element: HTMLElement | null) => chipFocus?.registerRemoveButton(optionKey, element),
    [chipFocus, optionKey]
  )

  const onRemoveKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['Enter', ' ', 'Backspace', 'Delete'].includes(event.key)) {
      return
    }

    // Prevent react-select from handling the key (e.g. opening the menu)
    event.preventDefault()
    event.stopPropagation()
    const values = getValue()
    const sibling = values[index + 1] ?? values[index - 1]
    chipFocus?.requestFocusAfterRemoval(sibling && getOptionKey(sibling))
    removeProps.onClick?.(event as any)
  }

  return (
    <components.MultiValue
      {...props}
      innerProps={{
        ...props.innerProps,
        style: {
          ...props.innerProps?.style,
          backgroundColor: '#DDE1E9',
        },
      }}
      removeProps={
        {
          ...removeProps,
          role: 'button',
          tabIndex: isDisabled ? -1 : 0,
          'aria-label': t('general.removeOption', { label: data.label }),
          ref: registerRemoveButton,
          onKeyDown: onRemoveKeyDown,
        } as any
      }
    />
  )
}

const CustomMultiValueLabel: FC<MultiValueGenericProps<ISelectOption, boolean>> = (props) => {
  return (
    <components.MultiValueLabel
      {...props}
      innerProps={{
        ...props.innerProps,
      }}
    >
      {props.data.label}
    </components.MultiValueLabel>
  )
}

export const Dropdown = observer(DropdownComponent)

// Match the design system clear-all button focus ring
const StyledDropdownWrapper = styled.div`
  .select__multi-value__remove:focus-visible {
    outline: 2px solid var(--postids-color-border-accent-low-contrast-default);
    outline-offset: 2px;
    border-radius: 50%;
  }
`
